import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
} from "@mui/material";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { debounce } from "lodash";
import React, {
  ChangeEvent,
  FC,
  memo,
  useEffect,
  useMemo,
  useState,
} from "react";
import { grey } from "@mui/material/colors";

interface CustomColumnMeta {
  isNumeric?: boolean;
}

type CustomColumnDef<TData> = ColumnDef<TData, unknown> & {
  meta?: CustomColumnMeta;
};

interface TableProps {
  data: any[];
  columns: CustomColumnDef<any>[];
  isFetching?: boolean;
  skeletonCount?: number;
  skeletonHeight?: number;
  pageCount?: number;
  page?: (page: number) => void;
  search?: (search: string) => void;
  searchLabel?: string;
  resetPage?: boolean;
  onSort?: (sortField: string, sortDirection: "asc" | "desc") => void;
  dense?: boolean;
  noDataText?: string;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  stickyHeader?: boolean;
  maxHeight?: number | string;
}

const Table: FC<TableProps> = ({
  data,
  columns,
  isFetching,
  skeletonCount = 10,
  skeletonHeight = 28,
  pageCount,
  search,
  page,
  searchLabel = "Otsi",
  resetPage,
  onSort,
  dense,
  noDataText,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  stickyHeader,
  maxHeight,
}) => {
  const [paginationPage, setPaginationPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    if (resetPage) {
      setPaginationPage(1);
    }
  }, [resetPage]);

  const createSortHandler = (property: string) => () => {
    setSorting((prev) => {
      const currentSort = prev.find((sort) => sort.id === property);
      const newDesc = currentSort ? !currentSort.desc : true;
      const direction = newDesc ? "desc" : "asc";

      if (onSort) {
        onSort(property, direction);
      }

      return [{ id: property, desc: newDesc }];
    });
  };

  const memoizedData = useMemo(() => data, [data]);
  const memoizedColumns = useMemo(() => columns, [columns]);

  const { getHeaderGroups, getRowModel, getAllColumns } = useReactTable({
    data: memoizedData,
    columns: memoizedColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount,
    state: { sorting },
    onSortingChange: setSorting,
  });

  const skeletons = Array.from({ length: skeletonCount }, (x, i) => i);

  const columnCount = getAllColumns().length;

  const noDataFound =
    !isFetching && (!memoizedData || memoizedData.length === 0);

  const handleSearchChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    search && search(e.target.value);
  };

  const handlePageChange = (
    event: ChangeEvent<unknown>,
    currentPage: number,
  ) => {
    setPaginationPage(currentPage === 0 ? 1 : currentPage);
    page?.(currentPage - 1);
  };

  return (
    <Box>
      {search && (
        <Box sx={{ mt: 2 }}>
          <TextField
            onChange={debounce(handleSearchChange, 1000)}
            size="small"
            label={searchLabel}
            variant="standard"
            sx={{ minWidth: "220px" }}
          />
        </Box>
      )}
      <TableContainer
        component={Paper}
        sx={{
          mt: 2,
          ...(stickyHeader &&
            (maxHeight !== undefined
              ? { overflow: "auto", maxHeight }
              : { overflow: "visible" })),
        }}
      >
        <MuiTable size="small" stickyHeader={stickyHeader}>
          {!isFetching && (
            <TableHead sx={{ bgcolor: grey[200] }}>
              {getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      align={header.column.columnDef.meta ? "right" : "left"}
                      sx={{
                        fontSize: dense ? "14px" : "initial",
                        whiteSpace: dense ? "nowrap" : "normal",
                        ...(stickyHeader && { backgroundColor: grey[200] }),
                      }}
                    >
                      {!header.column.getCanSort() ? (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      ) : (
                        <TableSortLabel
                          active={
                            !!sorting.find((sort) => sort.id === header.id)
                          }
                          direction={
                            sorting.find((sort) => sort.id === header.id)?.desc
                              ? "desc"
                              : "asc"
                          }
                          onClick={createSortHandler(header.id)}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
          )}
          <TableBody>
            {!isFetching
              ? getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <TableCell
                          key={cell.id}
                          align={cell.column.columnDef.meta ? "right" : "left"}
                          style={{
                            background: cell.row.original.color,
                            fontWeight: cell.row.original.bold ? 500 : 400,
                            fontSize: dense ? "12px" : "initial",
                            whiteSpace: dense ? "nowrap" : "normal",
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              : skeletons.map((_, index) => (
                  <TableRow key={index}>
                    <TableCell colSpan={columnCount}>
                      <Skeleton height={skeletonHeight} />
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {noDataFound && (
        <Box my={2} textAlign="center">
          {noDataText || "No data"}
        </Box>
      )}
      {!noDataFound && pageCount && page && (
        <Box
          sx={{ mt: 4 }}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
        >
          {pageSizeOptions && onPageSizeChange && pageSize !== undefined && (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="table-page-size-label">Rows per page</InputLabel>
              <Select
                labelId="table-page-size-label"
                label="Rows per page"
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
              >
                {pageSizeOptions.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Pagination
            shape="rounded"
            count={pageCount}
            page={paginationPage}
            onChange={handlePageChange}
          />
        </Box>
      )}
    </Box>
  );
};

export default memo(Table);
